import { Link } from "react-router-dom";
import { BUSINESS_ABN } from "@/config/business";
import { MapPin, Clock, Shield, CreditCard } from "lucide-react";

/**
 * Lumora footer — AU/NZ trust block per ACCC guidance.
 * ABN + AU address + AEST support hours + payment method icons are the
 * signals AU shoppers scan for in <5s.
 *
 * NOTE: ABN / street address / phone are placeholders — swap to real
 * business details before production launch.
 */
const PRODUCT_LINKS = [
  { href: "/#services", label: "Services" },
  { href: "/#pricing", label: "Pricing" },
  { to: "/score-your-ad?tab=roi", label: "ROI Calculator" },
  { to: "/score-your-ad", label: "Score Your Ad" },
  { to: "/blog", label: "Blog" },
];


const LEGAL_LINKS = [
  { to: "/privacy", label: "Privacy" },
  { to: "/terms", label: "Terms" },
  { to: "/refund-policy", label: "Refunds" },
];

const FooterLink = ({ href, to, label, className }) =>
  to ? <Link to={to} className={className}>{label}</Link> : <a href={href} className={className}>{label}</a>;

const LinkColumn = ({ title, links }) => (
  <div>
    <h4 className="font-bold text-white mb-4 text-sm">{title}</h4>
    <ul className="space-y-2 text-sm">
      {links.map((l) => (
        <li key={l.label}><FooterLink {...l} className="text-white/50 hover:text-white transition-colors" /></li>
      ))}
    </ul>
  </div>
);

const TrustBlock = () => (
  <div className="space-y-2 text-xs text-white/45" data-testid="au-nz-trust-block">
    <div className="flex items-start gap-2">
      <MapPin className="w-3.5 h-3.5 text-violet-400 shrink-0 mt-0.5" />
      <span>
        Lumora AI Pty Ltd{BUSINESS_ABN && <> · ABN&nbsp;<span className="font-mono text-white/60" data-testid="lumora-abn">{BUSINESS_ABN}</span></>}
        <br />
        <span className="text-white/40">Melbourne, VIC · Australia</span>
      </span>
    </div>
    <div className="flex items-start gap-2">
      <Clock className="w-3.5 h-3.5 text-violet-400 shrink-0 mt-0.5" />
      <span>Support · 9am–6pm AEST (Mon–Fri) · <a href="mailto:support@lumoraai.com.au" className="text-violet-300 hover:text-violet-200">support@lumoraai.com.au</a></span>
    </div>
    <div className="flex items-center gap-2 pt-2">
      <Shield className="w-3.5 h-3.5 text-violet-400 shrink-0" />
      <span>AU prices include GST · Cancel any time · Scripts checked for AU/NZ ad rules</span>
    </div>
  </div>
);

const PaymentStrip = () => (
  <div className="border-t border-white/5 pt-6 pb-4 flex flex-wrap items-center justify-between gap-4" data-testid="footer-payment-strip">
    <div className="flex items-center gap-2 text-xs text-white/40">
      <CreditCard className="w-4 h-4" />
      <span>Trusted payments · Visa · Mastercard · American Express · Afterpay · Apple&nbsp;Pay</span>
    </div>
    <p className="text-xs text-white/40">Powered by Stripe · PCI DSS · SSL secured</p>
  </div>
);

const LegalRow = () => (
  <div className="text-center pt-8 border-t border-white/5 mt-4 space-y-1">
    <p className="text-white/30 text-xs">&copy; {new Date().getFullYear()} Lumora AI Pty Ltd. All rights reserved.</p>
    <p className="text-white/25 text-[11px]">
      {LEGAL_LINKS.map((l, i) => (
        <span key={l.to}>
          {i > 0 && <span className="mx-1.5">·</span>}
          <Link to={l.to} className="hover:text-white/50 transition-colors">{l.label}</Link>
        </span>
      ))}
    </p>
  </div>
);

export const Footer = () => (
  <footer className="py-16 border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-4 gap-10 mb-12">
        <div className="md:col-span-2">
          <h3 className="text-2xl font-bold gradient-text mb-4">LUMORA</h3>
          <p className="text-white/50 max-w-sm mb-5 text-sm leading-relaxed">
            AI-powered UGC ad creation, built for Australian &amp; New Zealand e-commerce brands. Beat creative fatigue with 3–5 fresh variants a week.
          </p>
          <TrustBlock />
        </div>
        <LinkColumn title="Product" links={PRODUCT_LINKS} />
      </div>
      <PaymentStrip />
      <div className="text-center overflow-hidden">
        <h2 className="footer-brand select-none">LUMORA</h2>
      </div>
      <LegalRow />
    </div>
  </footer>
);
